import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { toast } from 'react-toastify';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { ServiceDataForm } from '../../../app/models/Service';
import useServiceService from '../../../app/services/hooks/useServiceService';
import ServiceForm from './forms/ServiceForm';
import LazyLoading from '../../../components/LazyLoading';

type SelectOpt = { id: number | string; name: string };

interface Props {
    serviceId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const ServiceEdit = ({ serviceId, onSaved, onCancel }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { editService, updateService, fetchingEditService, fetchingUpdateService } = useServiceService();
    const [service, setService] = useState<ServiceDataForm | null>(null);
    const [sectors, setSectors] = useState<SelectOpt[]>([]);
    const [itemCategories, setItemCategories] = useState<SelectOpt[]>([]);
    const [units, setUnits] = useState<SelectOpt[]>([]);
    const [taxes, setTaxes] = useState<SelectOpt[]>([]);
    const [errorFields, setErrorFields] = useState<any>();

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando servicio...');
        editService(serviceId, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setService(response.data?.service ?? null);
                setSectors(response.data?.sectors ?? []);
                setItemCategories(response.data?.item_categories ?? []);
                setUnits(response.data?.units ?? []);
                setTaxes(response.data?.taxes ?? []);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                onCancel?.();
            }
        });
    }, [serviceId]);

    const update = () => {
        if (!service) return;
        const requiredErrors: Record<string, string[]> = {};
        if (!service.name?.trim()) requiredErrors.name = ['El nombre es obligatorio.'];
        if (service.sector_id == null || service.sector_id === 0) requiredErrors.sector_id = ['El sector es obligatorio.'];
        if (service.item_category_id == null || service.item_category_id === 0) requiredErrors.item_category_id = ['La categoría es obligatoria.'];
        if (service.unit_id == null || service.unit_id === 0) requiredErrors.unit_id = ['La unidad de medida es obligatoria.'];
        if (Object.keys(requiredErrors).length > 0) {
            setErrorFields(requiredErrors);
            return;
        }
        setErrorFields(undefined);
        if (showLoading) showLoading('loading', 'Actualizando servicio...');
        const payload = {
            name: service.name,
            description: service.description ?? '',
            sector_id: service.sector_id ?? null,
            item_category_id: service.item_category_id ?? null,
            unit_id: service.unit_id ?? null,
            sales_name: service.sales_name ?? '',
            sales_tax_id: service.sales_tax_id ?? null,
            purchase_name: service.purchase_name ?? '',
            purchase_tax_id: service.purchase_tax_id ?? null
        };
        updateService(serviceId, payload, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message ?? 'Servicio actualizado.' });
                toast.success(response.message ?? 'Servicio actualizado.', { autoClose: 2500 });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
            },
            onFieldError: (res: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(res.data);
            }
        });
    };

    if (fetchingEditService || !service) {
        return <LazyLoading />;
    }

    return (
        <>
            <ServiceForm
                service={service}
                setService={setService}
                sectors={sectors}
                itemCategories={itemCategories}
                units={units}
                taxes={taxes}
                errorFields={errorFields}
            />
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={() => onCancel?.()} locked={fetchingUpdateService} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={update} locked={fetchingUpdateService} />
                </div>
            </div>
        </>
    );
};

export default ServiceEdit;
